import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../core/modules/global';
import { CREATE_STORE_DIALOG } from '../../../../core/constants/dialog';
import Spinner from '../../Spinner';
import { useCompanyCore } from '../../../../core/modules/company';
import { useWarehouseCore } from '../../../../core/modules/warehouse';
import { PostWarehousePayloadRequest } from '../../../../core/modules/warehouse/interfaces/post-warehouse';
import { useMasterCore } from '../../../../core/modules/master';
import { ERROR } from '../../../../core/constants/errorCode';

const schema = yup.object().shape({
  code: yup.string()
    .required('Campo requerido')
    .matches(/^[a-zA-Z0-9]{3}$/, 'El código debe tener exactamente 3 caracteres (letras y números)')
    .test('unique-code', 'Ya existe una bodega con este código', function(value) {
      // Esta validación se manejará en el backend
      return true;
    }),
  description: yup.string().required('Campo requerido'),
  address: yup.string().required('Campo requerido'),
  warehouse_type_id: yup.number().required('Campo requerido'),
  channel_type_ids: yup.array().of(yup.number().required()).min(1, 'Debe seleccionar al menos un canal').required('Campo requerido'),
});

const CreateDialog = () => {
  const { dialogs, closeDialog } = useGlobalCore();
  const { postWarehouse, getWarehouses, errorCode } = useWarehouseCore();
  const { warehouseTypes, channelTypes } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading]  = useState(false);

  const { control, formState, handleSubmit, resetField, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      channel_type_ids: []
    }
  });
  const { isValid, errors } = formState;

  const resetForm = useCallback(() => {
    resetField('address');
    resetField('channel_type_ids');
    resetField('code');
    resetField('description');
    resetField('warehouse_type_id');
    setValue('channel_type_ids', []); // Ensure it's reset to empty array
    // Limpiar errores al cerrar el diálogo
    if (errorCode) {
      // El errorCode se limpiará cuando se cierre el diálogo o se intente crear de nuevo
    }
    closeDialog(CREATE_STORE_DIALOG);
  }, [closeDialog, resetField, setValue, errorCode]);

  const onSubmit = useCallback(
    (values: any) => {
      if (companyId) {
        // Ensure channel_type_ids is a valid number array
        const channelIds = Array.isArray(values.channel_type_ids) 
          ? values.channel_type_ids.filter((id: any) => id !== undefined && id !== null)
          : [];
        const payload: PostWarehousePayloadRequest = {
          ...values,
          channel_type_ids: channelIds
        };
        
        console.log('Creando bodega con payload:', payload);
        setLoading(true);
        // El errorCode se limpiará automáticamente cuando se llame a postWarehouse (postWarehouseInitApiAction)
        postWarehouse(companyId, payload, () => {
          console.log('Bodega creada exitosamente');
          resetForm();
          getWarehouses(companyId);
          setLoading(false);
        }, () => {
          console.log('Error al crear bodega, errorCode:', errorCode);
          setLoading(false);
          // El errorCode se mantendrá para mostrarse en la UI
        });
      }
    },
    [companyId, postWarehouse, resetForm, getWarehouses, errorCode]
  );

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(CREATE_STORE_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Crear Bodega
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="code"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Código"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.code}
                          helperText={errors?.code?.message || "Ingrese exactamente 3 dígitos"}
                          required
                          fullWidth
                          inputProps={{
                            maxLength: 3,
                            style: { textTransform: 'uppercase' }
                          }}
                          onChange={(e) => {
                            field.onChange(e.target.value.toUpperCase());
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Descripción"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.description}
                          helperText={errors?.description?.message}
                          required
                          fullWidth
                          inputProps={{
                            style: { textTransform: 'uppercase' }
                          }}
                          onChange={(e) => {
                            field.onChange(e.target.value.toUpperCase());
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="warehouse_type_id"
                      control={control}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth required>
                          <InputLabel id="type-label">
                            Tipo
                          </InputLabel>
                          <Select
                            {...field}
                            labelId="type-label"
                            label="Tipo"
                            disabled={loading}
                            required
                          >
                            {warehouseTypes.map((type) => (
                              <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="channel_type_ids"
                      control={control}
                      render={({ field }) => (
                        <FormControl variant="outlined" fullWidth>
                          <InputLabel id="channel-label">
                            Canales
                          </InputLabel>
                          <Select
                            {...field}
                            value={Array.isArray(field.value) ? field.value : []}
                            labelId="channel-label"
                            label="Canales"
                            disabled={loading}
                            required
                            multiple
                            input={<OutlinedInput label="Canales" />}
                            renderValue={(selected) => {
                              const selectedIds = selected as number[];
                              return selectedIds
                                .map((id) => channelTypes.find((type) => type.id === id)?.name)
                                .filter(Boolean)
                                .join(', ');
                            }}
                          >
                            {channelTypes.map((type) => (
                              <MenuItem key={type.id} value={type.id}>
                                <Checkbox checked={field.value?.includes(type.id)} />
                                <ListItemText primary={type.name} />
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.channel_type_ids && (
                            <Typography color="error" className="text-sm mt-1">
                              {errors.channel_type_ids.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="address"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Dirección"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.address}
                          helperText={errors?.address?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>



                  {errorCode && (
                    <Typography color="error" className="text-md text-center mb-8">
                      {errorCode === ERROR.ERR_409 
                        ? 'Ya existe una bodega con este código' 
                        : errorCode === ERROR.ERR_400
                        ? 'Datos inválidos. Verifique que el código tenga exactamente 3 dígitos'
                        : 'Error al crear la bodega. Intente nuevamente'
                      }
                    </Typography>
                  )}
                  <div className="flex justify-center">
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || loading}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDialog;
